import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import ContactModal from "@/components/ContactModal";

const SECTIONS = [
  {
    title: "Overview",
    body: [
      "This policy covers paid HighRank placements — Discover Boost, Boost and Pro Boost — submitted through 'List your business' or a company's Boost page.",
      "Every Boost submission goes through admin review before any placement (Top Spot, Discover slot, leaderboard video, home screen video) is activated. Nothing goes live automatically.",
    ],
  },
  {
    title: "Before admin review",
    body: [
      "If your submission is still pending review and has not yet been approved, you may cancel and request a full refund at any time by contacting us.",
      "If your submission is rejected during review, you are automatically entitled to a full refund — no request needed.",
    ],
  },
  {
    title: "After activation",
    body: [
      "Once a placement has been approved and activated, it is considered delivered for the remainder of its tier duration (Discover Boost: 1 week · Boost / Pro Boost: 1 month).",
      "You may request a pro-rated refund for the unused portion of an active placement within 7 days of activation by contacting us.",
      "After 7 days from activation, purchases are non-refundable, consistent with our Terms of Service.",
      "No partial refunds are issued for unused days once a subscription period has been cancelled at your request — cancellation simply stops future renewal.",
    ],
  },
  {
    title: "Not eligible for refund",
    body: [
      "Placements deactivated by HighRank for violating our Terms of Service (e.g. misleading content, fraudulent listings).",
      "Requests made more than 7 days after a placement's activation date.",
      "Free-trial Boosts, since no payment was collected.",
    ],
  },
  {
    title: "How to request a refund",
    body: [
      <>Email us at{" "}<a href="mailto:support@highrank.space" className="text-emerald-400 hover:underline">support@highrank.space</a>{" "}or use the Contact Us form below with your business name, the tier purchased and the reason for your request.</>,
      "We aim to respond within 2 business days and, once approved, process refunds to your original payment method within 5–10 business days.",
    ],
  },
];

export default function RefundPolicy() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <main data-testid="refund-policy-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo
        title="Refund Policy — HighRank"
        siteName="HighRank"
        description="HighRank's refund policy for Discover Boost, Boost and Pro Boost placements."
      />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <FadeUp>
          <Link
            to="/"
            data-testid="refund-policy-back-link"
            className="inline-flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-400 transition-colors mb-8"
          >
            <ArrowLeft size={13} /> Home
          </Link>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-3">{"// Legal"}</p>
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-wide text-slate-100 mb-3">
            Refund Policy
          </h1>
          <p className="text-sm text-slate-500 mb-12 flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-400" /> Last updated: September 2026
          </p>
        </FadeUp>
        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.04}>
              <section>
                <h2 className="font-heading text-lg font-bold text-slate-100 mb-3">{s.title}</h2>
                <ul className="space-y-2.5">
                  {s.body.map((line, j) => (
                    <li key={j} className="text-sm text-slate-400 leading-relaxed flex gap-3">
                      <span className="text-emerald-500 mt-0.5 shrink-0">—</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.1}>
          <div className="mt-14 rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <p className="text-sm text-slate-400 leading-relaxed flex-1">
              Need to request a refund or have questions about this policy?
            </p>
            <button
              data-testid="refund-policy-contact-btn"
              onClick={() => setContactOpen(true)}
              className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all hover:opacity-90"
              style={{ backgroundColor: "#3FE08C", color: "#000" }}
            >
              Contact us
            </button>
          </div>
          <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
        </FadeUp>
      </section>
    </main>
  );
}
